import { jf } from "./job-model";

// ── The SQL-firewall models: NAMED MAPS of fields ──────────────────────────
// One FieldModel per firewall entity (table_metadata / table_fields /
// table_joins / table_validation / table_access). UI-free (only FieldDef via
// jf), same layering as JobDef/ScheduleDef. Column facts come from the zoo
// sources (godev zoo/continuum/continuum_zoo/slurps/cube/Table*.cube.zoo).
//
// FEATURE-20260716-sql-firewall-policy-editor

/** The nine access modes the firewall evaluates (pkg/rpc/db access_modes). */
export const ACCESS_MODES = [
  "select-sql",
  "insert-sql",
  "update-sql",
  "delete-sql",
  "fetch-data",
  "insert-data",
  "update-data",
  "delete-data",
  "upsert-data",
] as const;

/** The subset validation rules run under (insert/update writes only). */
export const VALIDATION_ACCESS_MODES = ["insert-data", "update-data"] as const;

export const TableMetadataDef = {
  table_name: jf({ name: "table_name", label: "Table Name", type: "string", required: true }),
  schema_name: jf({ name: "schema_name", label: "Schema", type: "string", required: true }),
  actual_table_name: jf({ name: "actual_table_name", label: "Physical Table Name", type: "string" }),
  description: jf({ name: "description", label: "Description", type: "string" }),
  external_maintainer: jf({ name: "external_maintainer", label: "Owner", type: "string", editable: false }),
};

export const TableFieldsDef = {
  table_uid: jf({ name: "table_uid", label: "Table", type: "string", required: true }),
  column_name: jf({ name: "column_name", label: "Column Name", type: "string", required: true }),
  field_kind: jf({
    name: "field_kind",
    label: "Kind",
    type: "enum",
    required: true,
    enumValues: [
      { value: "calculated", text: "calculated (read: SELECT expression)" },
      { value: "generated", text: "generated (write: value expression)" },
    ],
  }),
  select_expression: jf({ name: "select_expression", label: "Select Expression", type: "string" }),
  expression: jf({ name: "expression", label: "Generate Expression", type: "string" }),
  generate_mode: jf({
    name: "generate_mode",
    label: "Generate Mode",
    type: "enum",
    enumValues: [
      { value: "if-not-provided", text: "if-not-provided" },
      { value: "always", text: "always" },
    ],
  }),
  access_modes: jf({ name: "access_modes", label: "Access Modes", type: "json" }),
  include_in_star: jf({ name: "include_in_star", label: "Include in SELECT *", type: "boolean" }),
  priority: jf({ name: "priority", label: "Priority", type: "number" }),
  external_maintainer: jf({ name: "external_maintainer", label: "Owner", type: "string", editable: false }),
};

export const TableJoinsDef = {
  from_table_uid: jf({ name: "from_table_uid", label: "From Table", type: "string", required: true }),
  join_name: jf({ name: "join_name", label: "Join Name", type: "string", required: true }),
  to_table_uid: jf({ name: "to_table_uid", label: "To Table", type: "enum", required: true }),
  join_expression: jf({
    name: "join_expression",
    label: "Join Expression",
    type: "string",
    required: true,
  }),
  join_type: jf({
    name: "join_type",
    label: "Join Type",
    type: "enum",
    required: true,
    enumValues: [
      { value: "LEFT", text: "LEFT" },
      { value: "INNER", text: "INNER" },
    ],
  }),
  external_maintainer: jf({ name: "external_maintainer", label: "Owner", type: "string", editable: false }),
};

export const TableValidationDef = {
  table_uid: jf({ name: "table_uid", label: "Table", type: "string", required: true }),
  validation_name: jf({ name: "validation_name", label: "Validation Name", type: "string", required: true }),
  access_modes: jf({ name: "access_modes", label: "Runs On", type: "json", required: true }),
  trigger_columns: jf({ name: "trigger_columns", label: "Trigger Columns", type: "json" }),
  query: jf({ name: "query", label: "Query", type: "string", required: true }),
  valid_when_no_rows: jf({ name: "valid_when_no_rows", label: "Valid When No Rows", type: "boolean" }),
  message: jf({ name: "message", label: "Failure Message", type: "string", required: true }),
  priority: jf({ name: "priority", label: "Priority", type: "number" }),
  external_maintainer: jf({ name: "external_maintainer", label: "Owner", type: "string", editable: false }),
};

export const TableAccessDef = {
  table_uid: jf({ name: "table_uid", label: "Table", type: "string" }), // null = GLOBAL rule
  name: jf({ name: "name", label: "Rule Name", type: "string", required: true }),
  usergroup_uid: jf({ name: "usergroup_uid", label: "User Group", type: "enum" }), // null = all users
  access_modes: jf({ name: "access_modes", label: "Access Modes", type: "json", required: true }),
  granted: jf({ name: "granted", label: "Granted", type: "boolean" }),
  terminal: jf({ name: "terminal", label: "Terminal", type: "boolean" }),
  priority: jf({ name: "priority", label: "Priority", type: "number" }),
  row_filter: jf({ name: "row_filter", label: "Row Filter (SQL)", type: "string" }),
  reason: jf({ name: "reason", label: "Reason", type: "string" }),
  external_maintainer: jf({ name: "external_maintainer", label: "Owner", type: "string", editable: false }),
};

export const EndpointAuthDef = {
  endpoint_pattern: jf({
    name: "endpoint_pattern",
    label: "Endpoint Pattern",
    type: "string",
    required: true,
  }),
  usergroup_uid: jf({ name: "usergroup_uid", label: "User Group", type: "enum" }), // null = all users
  granted: jf({ name: "granted", label: "Granted", type: "boolean" }),
  priority: jf({ name: "priority", label: "Priority", type: "number" }),
  reason: jf({ name: "reason", label: "Reason", type: "string" }),
  expires_at: jf({ name: "expires_at", label: "Expires At", type: "string" }),
  external_maintainer: jf({
    name: "external_maintainer",
    label: "External Maintainer",
    type: "string",
    editable: false,
  }),
};
